import { useMemo } from 'react'
import useUserContext from 'shared/users/contexts/useUserContext'
import {
  ActionRuleContext,
  getEnsPresentationNotificationConfig,
  getPreLodgedDeclarationConfig,
  isAmendButtonDisabled,
  isCancelButtonDisabled,
  isEnsPresentationNotificationAvailable,
  isPreLodgedPresentationNotificationAvailable,
  isSubmitDisabled,
} from '../../models/actionRules'
import { useFetchTransitOperationLogs } from '../../event-log/api/fetchTransitOperationLogs'
import {
  countByMessageCode,
  hasMessageCode,
  isPreLodgedPresentationNotificationAwaitingAnswer,
  isPreLodgedPresentationNotificationAwaitingCorrection,
  isSubmissionAwaitingAnswer,
  readMessageLog,
} from '../../event-log/models/messageLogSignals'
import { useDeclarationEditorContext } from '../contexts/DeclarationEditorContext'

function useDeclarationEditorActionRules() {
  const { declarationData, editorData } = useDeclarationEditorContext()
  const transitOperation = declarationData?.transitOperation

  const { data: eventLogs } = useFetchTransitOperationLogs(transitOperation?.id ?? null)

  const country = editorData?.country ?? ''
  const security = editorData?.security ?? ''

  const messageLog = useMemo(() => readMessageLog(eventLogs, country), [eventLogs, country])

  const {
    hasSubmissionConfirmedMessage,
    hasMRNAllocatedMessage,
    hasReleaseWithoutMrnMessage,
    hasEnsPresentationNotificationAcceptedMessage,
    isEnsPresentationNotificationSubmissionPending,
    isPreLodgedPresentationNotificationPending,
    isPreLodgedPresentationNotificationCorrectionPending,
  } = useMemo(() => {
    const ensPresentationNotificationConfig = getEnsPresentationNotificationConfig(country)
    const preLodgedDeclarationConfig = getPreLodgedDeclarationConfig(country)
    const ensPending = ensPresentationNotificationConfig?.pending

    const isEnsSubmissionPending = () => {
      if (!ensPending) {
        return false
      }
      if (ensPending.strategy === 'ordered') {
        return isSubmissionAwaitingAnswer(messageLog, ensPending.submissionSuffix, ensPending.closingSuffixes)
      }
      return countByMessageCode(messageLog, ensPending.submissionSuffix) > countByMessageCode(messageLog, ...ensPending.closingSuffixes)
    }

    return {
      hasSubmissionConfirmedMessage: hasMessageCode(messageLog, 'IE928'),
      hasMRNAllocatedMessage: hasMessageCode(messageLog, 'IE028'),
      hasReleaseWithoutMrnMessage: hasMessageCode(
        messageLog,
        'IEA29',
        ...(preLodgedDeclarationConfig?.extraPresentationCompletedSuffixes ?? []),
      ),
      hasEnsPresentationNotificationAcceptedMessage: ensPresentationNotificationConfig?.acceptedSuffix
        ? hasMessageCode(messageLog, ensPresentationNotificationConfig.acceptedSuffix)
        : false,
      isEnsPresentationNotificationSubmissionPending: isEnsSubmissionPending(),
      isPreLodgedPresentationNotificationPending: isPreLodgedPresentationNotificationAwaitingAnswer(
        messageLog,
        preLodgedDeclarationConfig?.extraClosingSuffixes,
      ),
      isPreLodgedPresentationNotificationCorrectionPending:
        isPreLodgedPresentationNotificationAwaitingCorrection(messageLog),
    }
  }, [messageLog, country])

  const { user } = useUserContext()

  const ctx: ActionRuleContext = useMemo(() => ({
    status: transitOperation?.status ?? '',
    lrn: transitOperation?.lrn ?? '',
    mrn: transitOperation?.mrn ?? '',
    country,
    security,
    role: user?.role ?? '',
    additionalDeclarationType: transitOperation?.additionalDeclarationType ?? '',
    hasSubmissionConfirmedMessage,
    hasMRNAllocatedMessage,
    hasReleaseWithoutMrnMessage,
    hasEnsPresentationNotificationAcceptedMessage,
    isEnsPresentationNotificationSubmissionPending,
    isPreLodgedPresentationNotificationPending,
    isPreLodgedPresentationNotificationCorrectionPending,
  }), [
    country,
    security,
    hasSubmissionConfirmedMessage,
    hasMRNAllocatedMessage,
    hasReleaseWithoutMrnMessage,
    hasEnsPresentationNotificationAcceptedMessage,
    isEnsPresentationNotificationSubmissionPending,
    isPreLodgedPresentationNotificationPending,
    isPreLodgedPresentationNotificationCorrectionPending,
    transitOperation?.additionalDeclarationType,
    transitOperation?.lrn,
    transitOperation?.mrn,
    transitOperation?.status,
    user?.role,
  ])

  return useMemo(() => ({
    isSubmitDisabled: isSubmitDisabled(ctx),
    isAmendDisabled: isAmendButtonDisabled(ctx),
    isCancelDisabled: isCancelButtonDisabled(ctx),
    isEnsPresentationNotificationAvailable: isEnsPresentationNotificationAvailable(ctx),
    isPreLodgedPresentationNotificationAvailable: isPreLodgedPresentationNotificationAvailable(ctx),
  }), [ctx])
}

export default useDeclarationEditorActionRules
